/**
 * i18next initialization.
 *
 * Languages are statically imported (13 small JSON files) so they're available
 * before first paint and don't trigger waterfall loads. Language detection
 * order: `localStorage` (key `caliu-language`) → `navigator`. No `htmlTag`:
 * `applyHtmlLang` mirrors the current language there, so "system default"
 * would detect the language it is switching away from.
 *
 * A `languageChanged` handler keeps `document.documentElement.lang` in sync
 * for assistive tech and CSS `:lang(...)` selectors.
 */

import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { SUPPORTED_LOCALES } from '@caliu/types'

import en from '../locales/en/translation.json'
import es from '../locales/es/translation.json'
import ca from '../locales/ca/translation.json'
import fr from '../locales/fr/translation.json'
import de from '../locales/de/translation.json'
import it from '../locales/it/translation.json'
import pt from '../locales/pt/translation.json'
import nl from '../locales/nl/translation.json'
import ja from '../locales/ja/translation.json'
import zhCN from '../locales/zh-CN/translation.json'
import ko from '../locales/ko/translation.json'
import zhTW from '../locales/zh-TW/translation.json'
import ru from '../locales/ru/translation.json'

export const LANGUAGE_STORAGE_KEY = 'caliu-language'

/**
 * A missing key and a missing interpolation value both leak raw text to the
 * user (`pro.perMonth`, `{{price}}`), so report them instead of rendering them
 * silently. Never throws: a broken string must not take the UI down.
 *
 * Deduped per session because these fire on every render of the offending
 * component.
 */
const reportedI18nIssues = new Set<string>()

const reportI18nIssue = (message: string): void => {
  if (reportedI18nIssues.has(message)) return
  reportedI18nIssues.add(message)
  Sentry.captureException(new Error(message))
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      ca: { translation: ca },
      fr: { translation: fr },
      de: { translation: de },
      it: { translation: it },
      pt: { translation: pt },
      nl: { translation: nl },
      ja: { translation: ja },
      'zh-CN': { translation: zhCN },
      ko: { translation: ko },
      'zh-TW': { translation: zhTW },
      ru: { translation: ru },
    },
    supportedLngs: [...SUPPORTED_LOCALES],
    // `es-AR` → `es` still resolves through the language-part fallback. Do not
    // set `nonExplicitSupportedLngs`: it checks only the language part, and
    // `zh` is not in the list, so `zh-CN` / `zh-TW` would fall back to English.
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // i18next takes the first exact match across all detected codes before
      // trying language parts, so `['ca-ES', 'en']` would pick `en`. Reduce
      // each code to its language part unless it is supported as-is (`zh-TW`).
      convertDetectedLanguage: (lng: string) =>
        (SUPPORTED_LOCALES as readonly string[]).includes(lng) ? lng : lng.split('-')[0],
      caches: [], // we manage persistence explicitly via the UI store
    },
    returnNull: false,
    saveMissing: true, // required for `missingKeyHandler` to fire
    missingKeyHandler: (lngs, ns, key) => {
      reportI18nIssue(`i18n missing key "${ns}:${key}" for ${lngs.join(', ')}`)
    },
    missingInterpolationHandler: (text, value) => {
      reportI18nIssue(`i18n missing interpolation ${String(value[0])} in "${text}"`)
    },
  })

const applyHtmlLang = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng
  }
}

i18n.on('languageChanged', applyHtmlLang)
// Run once after init so SSR fallback `<html lang="en">` is corrected to the
// detected locale on first render.
applyHtmlLang(i18n.resolvedLanguage ?? i18n.language ?? 'en')

export default i18n
